import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Form,
  Input,
  Select,
  Modal,
  message,
  Tag,
  Space,
  Descriptions,
  Typography,
  Row,
  Col,
  Switch,
  InputNumber,
  Tooltip,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  QrcodeOutlined,
  SettingOutlined,
  KeyOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import axios from 'axios';
import dayjs from 'dayjs';

// Create axios instance for JWT authentication (for admin endpoints)
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '/api',
  timeout: 10000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Just return the error without redirecting
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const Members = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('qris');

  // QRIS Management State
  const [qrisMerchants, setQrisMerchants] = useState([]);
  const [qrisModalVisible, setQrisModalVisible] = useState(false);
  const [editingQris, setEditingQris] = useState(null);

  // Callback Management State
  const [callbackConfigs, setCallbackConfigs] = useState({});
  const [callbackModalVisible, setCallbackModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // API Keys State
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);

  // Forms
  const [qrisForm] = Form.useForm();
  const [callbackForm] = Form.useForm();
  const [apiKeyForm] = Form.useForm();

  useEffect(() => {
    fetchQrisMerchants();
  }, []);

  // Fetch QRIS Merchants
  const fetchQrisMerchants = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/api/member-management/qris-merchants');
      setQrisMerchants(response.data.qris_merchants);
    } catch (error) {
      console.error('Error fetching QRIS merchants:', error);

      // Use mock data as fallback
      const mockQrisMerchants = [
        {
          id: 1,
          user_id: 7,
          member_id: 'MBR000007',
          merchant_name: 'Warung Makan Bahagia',
          merchant_id: 'ID1234567890123456',
          terminal_id: 'TRM001',
          mcc: '5812',
          location: 'Jakarta Selatan',
          status: 'active',
          min_amount: 10000,
          max_amount: 5000000,
          daily_limit: 50000000,
          created_at: '2024-11-18T10:00:00Z'
        },
        {
          id: 2,
          user_id: 8,
          member_id: 'MBR000008',
          merchant_name: 'Toko Elektronik Jaya',
          merchant_id: 'ID2345678901234567',
          terminal_id: 'TRM002',
          mcc: '5732',
          location: 'Bandung',
          status: 'active',
          min_amount: 25000,
          max_amount: 10000000,
          daily_limit: 100000000,
          created_at: '2024-11-18T09:30:00Z'
        }
      ];

      setQrisMerchants(mockQrisMerchants);
      console.log('Using fallback mock data for QRIS merchants');
    } finally {
      setLoading(false);
    }
  };

  // Handle create/edit QRIS merchant
  const handleQrisSubmit = async (values) => {
    try {
      if (editingQris) {
        await adminApi.put(`/api/member-management/qris-merchants/${editingQris.id}`, values);
        message.success('QRIS merchant updated successfully');
      } else {
        await adminApi.post('/api/member-management/qris-merchants', values);
        message.success('QRIS merchant created successfully');
      }

      setQrisModalVisible(false);
      setEditingQris(null);
      qrisForm.resetFields();
      fetchQrisMerchants();
    } catch (error) {
      console.error('Error saving QRIS merchant:', error);
      message.error('Failed to save QRIS merchant');
    }
  };

  // Handle callback configuration
  const fetchCallbackConfig = async (memberId) => {
    try {
      const response = await adminApi.get(`/api/member-management/callback-config/${memberId}`);
      setCallbackConfigs(prev => ({
        ...prev,
        [memberId]: response.data
      }));
      return response.data;
    } catch (error) {
      console.error('Error fetching callback config:', error);
      message.error('Failed to fetch callback configuration');
    }
  };

  const handleCallbackSubmit = async (values) => {
    try {
      const response = await adminApi.put(
        `/api/member-management/callback-config/${selectedMember}`,
        values
      );

      message.success('Callback configuration updated successfully');

      if (response.data.callback_secret) {
        Modal.info({
          title: 'New Callback Secret Generated',
          content: (
            <div>
              <Paragraph>Your new callback secret:</Paragraph>
              <Paragraph code copyable>{response.data.callback_secret}</Paragraph>
              <Paragraph type="warning">
                Please save this secret securely. You won't be able to see it again.
              </Paragraph>
            </div>
          )
        });
      }

      setCallbackModalVisible(false);
      callbackForm.resetFields();
      setSelectedMember(null);
    } catch (error) {
      console.error('Error updating callback config:', error);
      message.error('Failed to update callback configuration');
    }
  };

  // Handle API Keys
  const fetchApiKeys = async (memberId) => {
    try {
      const response = await adminApi.get(`/api/member-management/api-keys/${memberId}`);
      setApiKeys(response.data.api_keys);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      message.error('Failed to fetch API keys');
    }
  };

  const handleCreateApiKey = async (values) => {
    try {
      const response = await adminApi.post('/api/member-management/api-keys', {
        ...values,
        member_id: selectedMember
      });

      Modal.info({
        title: 'API Key Created',
        content: (
          <div>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="API Key">
                <Text code copyable>{response.data.api_key}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="API Secret">
                <Text code copyable>{response.data.api_secret}</Text>
              </Descriptions.Item>
            </Descriptions>
            <Paragraph type="warning">
              Please save these credentials securely. You won't be able to see the secret again.
            </Paragraph>
          </div>
        )
      });

      setApiKeyModalVisible(false);
      apiKeyForm.resetFields();
      fetchApiKeys(selectedMember);
    } catch (error) {
      console.error('Error creating API key:', error);
      message.error('Failed to create API key');
    }
  };

  // QRIS Merchants Table Columns
  const qrisColumns = [
    {
      title: 'Merchant Name',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
    },
    {
      title: 'Merchant ID',
      dataIndex: 'merchant_id',
      key: 'merchant_id',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Terminal ID',
      dataIndex: 'terminal_id',
      key: 'terminal_id',
      render: (text) => <Text code>{text || '-'}</Text>
    },
    {
      title: 'MCC',
      dataIndex: 'mcc',
      key: 'mcc',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingQris(record);
                qrisForm.setFieldsValue(record);
                setQrisModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Configure Callback">
            <Button
              type="link"
              icon={<SettingOutlined />}
              onClick={async () => {
                setSelectedMember(record.member_id);
                const config = await fetchCallbackConfig(record.member_id);
                callbackForm.setFieldsValue(config);
                setCallbackModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Manage API Keys">
            <Button
              type="link"
              icon={<KeyOutlined />}
              onClick={() => {
                setSelectedMember(record.member_id);
                fetchApiKeys(record.member_id);
                setActiveTab('apikeys');
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Member Management</Title>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* QRIS Merchants Tab */}
        <TabPane
          tab={
            <span>
              <QrcodeOutlined />
              QRIS Merchants
            </span>
          }
          key="qris"
        >
          <Card
            title="QRIS Merchant List"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingQris(null);
                  qrisForm.resetFields();
                  setQrisModalVisible(true);
                }}
              >
                Add QRIS Merchant
              </Button>
            }
          >
            <Table
              columns={qrisColumns}
              dataSource={qrisMerchants}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1000 }}
            />
          </Card>
        </TabPane>

        {/* API Keys Management Tab */}
        {selectedMember && (
          <TabPane
            tab={
              <span>
                <KeyOutlined />
                API Keys
              </span>
            }
            key="apikeys"
          >
            <Card
              title={`API Keys for Member ${selectedMember}`}
              extra={
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setApiKeyModalVisible(true)}
                >
                  Create API Key
                </Button>
              }
            >
              <Table
                dataSource={apiKeys}
                rowKey="id"
                columns={[
                  {
                    title: 'Key Name',
                    dataIndex: 'key_name',
                    key: 'key_name'
                  },
                  {
                    title: 'API Key',
                    dataIndex: 'api_key',
                    key: 'api_key',
                    render: (text) => <Text code>{text}</Text>
                  },
                  {
                    title: 'Status',
                    dataIndex: 'is_active',
                    key: 'is_active',
                    render: (active) => (
                      <Tag color={active ? 'green' : 'red'}>
                        {active ? 'Active' : 'Inactive'}
                      </Tag>
                    )
                  },
                  {
                    title: 'Last Used',
                    dataIndex: 'last_used_at',
                    key: 'last_used_at',
                    render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : 'Never'
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_, record) => (
                      <Button
                        type="link"
                        danger
                        onClick={async () => {
                          try {
                            await adminApi.put(`/api/member-management/api-keys/${record.id}/deactivate`);
                            message.success('API key deactivated');
                            fetchApiKeys(selectedMember);
                          } catch (error) {
                            message.error('Failed to deactivate API key');
                          }
                        }}
                      >
                        Deactivate
                      </Button>
                    )
                  }
                ]}
              />
            </Card>
          </TabPane>
        )}
      </Tabs>

      {/* QRIS Merchant Modal */}
      <Modal
        title={editingQris ? 'Edit QRIS Merchant' : 'Add QRIS Merchant'}
        open={qrisModalVisible}
        onCancel={() => {
          setQrisModalVisible(false);
          setEditingQris(null);
          qrisForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={qrisForm}
          layout="vertical"
          onFinish={handleQrisSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Member ID"
                name="user_id"
                rules={[{ required: true, message: 'Member ID is required' }]}
              >
                <Input placeholder="Enter member ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Merchant Name"
                name="merchant_name"
                rules={[{ required: true, message: 'Merchant name is required' }]}
              >
                <Input placeholder="Enter merchant name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Merchant ID (NMID)"
                name="merchant_id"
                rules={[{ required: true, message: 'Merchant ID is required' }]}
              >
                <Input placeholder="Enter NMID" disabled={!!editingQris} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Terminal ID"
                name="terminal_id"
              >
                <Input placeholder="Enter terminal ID" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="MCC"
                name="mcc"
                rules={[{ required: true, message: 'MCC is required' }]}
              >
                <Input placeholder="Enter MCC" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
              >
                <Select placeholder="Select status">
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="suspended">Suspended</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Location"
            name="location"
            rules={[{ required: true, message: 'Location is required' }]}
          >
            <Input placeholder="Enter location" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Min Amount"
                name="min_amount"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Minimum amount"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Max Amount"
                name="max_amount"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Maximum amount"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Daily Limit"
                name="daily_limit"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Daily limit"
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingQris ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => {
                setQrisModalVisible(false);
                setEditingQris(null);
                qrisForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Callback Configuration Modal */}
      <Modal
        title="Callback Configuration"
        open={callbackModalVisible}
        onCancel={() => {
          setCallbackModalVisible(false);
          setSelectedMember(null);
          callbackForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={callbackForm}
          layout="vertical"
          onFinish={handleCallbackSubmit}
        >
          <Form.Item
            label="Payment Callback URL"
            name="payment_callback_url"
          >
            <Input placeholder="https://your-domain.com/webhooks/payment" />
          </Form.Item>

          <Form.Item
            label="Settlement Callback URL"
            name="settlement_callback_url"
          >
            <Input placeholder="https://your-domain.com/webhooks/settlement" />
          </Form.Item>

          <Form.Item
            label="Refund Callback URL"
            name="refund_callback_url"
          >
            <Input placeholder="https://your-domain.com/webhooks/refund" />
          </Form.Item>

          <Divider />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Enable Callbacks"
                name="callback_enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Use Signature"
                name="use_signature"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Max Retry Attempts"
                name="max_retry_attempts"
              >
                <InputNumber min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Retry Interval (seconds)"
                name="retry_interval"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Timeout (seconds)"
                name="timeout_seconds"
              >
                <InputNumber min={1} max={300} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Email Notifications"
                name="email_notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="SMS Notifications"
                name="sms_notifications"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update Configuration
              </Button>
              <Button onClick={() => {
                setCallbackModalVisible(false);
                setSelectedMember(null);
                callbackForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* API Key Creation Modal */}
      <Modal
        title="Create API Key"
        open={apiKeyModalVisible}
        onCancel={() => {
          setApiKeyModalVisible(false);
          apiKeyForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={apiKeyForm}
          layout="vertical"
          onFinish={handleCreateApiKey}
        >
          <Form.Item
            label="Key Name"
            name="key_name"
            rules={[{ required: true, message: 'Key name is required' }]}
          >
            <Input placeholder="Enter key name" />
          </Form.Item>

          <Form.Item
            label="Permissions"
            name="permissions"
          >
            <Select
              mode="multiple"
              placeholder="Select permissions"
              options={[
                { label: 'Create Payment', value: 'create_payment' },
                { label: 'Read Transactions', value: 'read_transactions' },
                { label: 'Webhook Access', value: 'webhook_access' },
                { label: 'Settlement Access', value: 'settlement_access' }
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Allowed IPs (comma separated)"
            name="allowed_ips"
          >
            <TextArea placeholder="192.168.1.1, 10.0.0.1" rows={3} />
          </Form.Item>

          <Form.Item
            label="Rate Limit (per minute)"
            name="rate_limit_per_minute"
          >
            <InputNumber min={1} max={1000} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create API Key
              </Button>
              <Button onClick={() => {
                setApiKeyModalVisible(false);
                apiKeyForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Members;